import { useAppBarContent } from "@/contexts/AppBarContentContext";
import { useLazoView } from "@/contexts/CurrentLazoViewContext";
import { useNavbarBackground } from "@/contexts/NavbarBackgroundContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { useResponsive } from "@/hooks/useResponsive";
import { Badge, Box, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import InboxLogo from "../../../assets/images/Inbox-logo-v2.svg";
import NotificationsLogo from "../../../assets/images/notification-lazo-one-drawer-logo.svg";
import { LanguageSwitch } from "../common/LanguageSwitch";
import { AccountMenu } from "./AccountMenu";

function NavbarContainer(props: React.PropsWithChildren & { backgroundColor: string; title: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        padding: "1rem 2rem",
        backgroundColor: "rgba(255, 255, 255, 0.8)", // Keep glass effect if desired
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${Colors.LIGHT_GRAY_EBEBEB}`,
      }}
    >
      <Box sx={{ display: { xs: "none", md: "block" } }}>{props.title}</Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", ml: "auto" }}>{props.children}</Box>
    </Box>
  );
}

interface NavbarItemProps {
  to: string;
}

function NavbarItemContainer({ children, to }: React.PropsWithChildren<NavbarItemProps>) {
  return (
    <Link
      component={RouterLink}
      to={to}
      sx={{ display: "flex", alignItems: "center", gap: "0.5rem", textDecoration: "none", color: "#4A4459", fontWeight: 600 }}
    >
      {children}
    </Link>
  );
}

export function Navbar() {
  const { t } = useTranslation("navigation");
  const { isMobile } = useResponsive();
  const { isPortal } = useLazoView();
  const { workspaceState } = useTenantMemberships();
  const isLazoAdminOrKam = workspaceState?.isLazoAdminOrKam ?? false;
  const { amountUnread, amountChatUnread } = useNotifications();
  const { navbarBackground } = useNavbarBackground();
  const { appBarContent } = useAppBarContent();

  return (
    <NavbarContainer backgroundColor={navbarBackground} title={appBarContent}>
      {!isMobile && <LanguageSwitch />}
      {!isMobile && (
        <NavbarItemContainer to={isPortal ? LazoPaths.PORTAL_NOTIFICATIONS : LazoPaths.NOTIFICATIONS}>
          <Badge badgeContent={amountUnread} sx={{ "& .MuiBadge-badge": { backgroundColor: Colors.PURPLE_6750A4, color: "#fff" } }}>
            <img src={NotificationsLogo.src} alt="" />
          </Badge>
          <span className="span-with-icon">{t("drawer.lazoOne.notifications")}</span>
        </NavbarItemContainer>
      )}

      {!isMobile && (
        <NavbarItemContainer
          to={isLazoAdminOrKam ? LazoPaths.PORTAL_KAM_INBOX : isPortal ? LazoPaths.PORTAL_INBOX : LazoPaths.ONE_COMMUNICATIONS}
        >
          <Badge badgeContent={amountChatUnread} sx={{ "& .MuiBadge-badge": { backgroundColor: Colors.PURPLE_6750A4, color: "#fff" } }}>
            <img alt={"communications-logo"} src={InboxLogo.src} />
          </Badge>
          <span className="span-with-icon">{t("drawer.lazoOne.inbox")}</span>
        </NavbarItemContainer>
      )}

      <AccountMenu />
    </NavbarContainer>
  );
}
